import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { DomainCard } from '../components/demoCenter/DomainCard';
import { DomainDetailDialog } from '../components/demoCenter/DomainDetailDialog';
import { GovernmentFeature } from '../components/demoCenter/GovernmentFeature';
import { DemoFoundation } from '../components/demoCenter/DemoFoundation';
import { demoDomains, demoMilestoneNote, demoStatusLabels, sharedFoundationCopy, type DemoDomain } from '../data/demoCenter';
import { fadeUp } from '../utils/motion';
export function NoetivraDemoCenterPage() {
  const [openDomain, setOpenDomain] = useState<DemoDomain | null>(null);
  const [highlightedId, setHighlightedId] = useState<DemoDomain['id'] | null>(null);

  // A domain anchor (e.g. #talent) highlights its card; planned domains also open their detail.
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const domain = demoDomains.find(item => item.id === hash);
    if (!domain) return;
    setHighlightedId(domain.id);
    if (domain.status === 'planned') {
      const timer = window.setTimeout(() => setOpenDomain(domain), 250);
      return () => window.clearTimeout(timer);
    }
  }, []);
  const closeDialog = useCallback(() => setOpenDomain(null), []);
  return <>
      <PageHero eyebrow="ONE PLATFORM. PURPOSE-BUILT WORKSPACES." title="Seven domains. One connected foundation." lead="Purpose-built workspaces for knowledge, capability and action. Explore complete sample workflows with domain-specific records and responsibilities." body={[sharedFoundationCopy, 'Choose a domain, then pick the workspace designed for its participants, professionals or administrators.']} backLink={{
      href: '/noetivra',
      label: 'Noetivra'
    }} data-id="element-3101" data-mp="2x2d98zbwvcxp">
        <div className="mt-8" data-id="element-3102" data-mp="29csme5cmzvj5">
          <a href="#domains" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3103" data-mp="29bhh213jmfpx">
            Choose a domain
            <ArrowDown size={18} aria-hidden="true" data-id="element-3104" data-mp="n0s9jobaiy3f" />
          </a>
        </div>
      </PageHero>

      <section id="domains" aria-labelledby="domains-title" className="scroll-mt-20 bg-white border-b border-line py-20 lg:py-28" data-id="element-3105" data-mp="r0snl7uh979y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3106" data-mp="3vfsk34ehlo3e">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 mb-12" data-id="element-3107" data-mp="3vg284mfz1l6g">
            <div className="lg:col-span-5" data-id="element-3108" data-mp="3aop1ew5bxpt">
              <p className="text-sm font-medium text-teal mb-3" data-id="element-3109" data-mp="8bm43ilv0bn3">Choose a domain</p>
              <h2 id="domains-title" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-3110" data-mp="1er6gt1ostc64">
                Explore Noetivra in your field
              </h2>
              <p className="mt-4 text-ink-muted leading-relaxed" data-id="element-3111" data-mp="1ttwvcfn7oc0a">
                Each card opens that domain’s own workspace chooser in a new tab. Records are fictional and kept separately for each domain.
              </p>
            </div>
            <dl className="lg:col-span-7 lg:pt-8 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-3112" data-mp="1k326d3ikyyb5">
              {(['interactive', 'scoped'] as const).map(status => <div key={status} data-id="element-3113" data-mp="1b3f1g7qn7adc">
                  <dt className="font-heading font-semibold text-ink-dark" data-id="element-3114" data-mp="1b4ht4hvshgub">{demoStatusLabels[status].label}</dt>
                  <dd className="mt-1 text-ink-muted leading-relaxed" data-id="element-3115" data-mp="1630xp1w2qml4">{demoStatusLabels[status].meaning}</dd>
                </div>)}
            </dl>
          </motion.div>

          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-3116" data-mp="x58p0gkuyd6p">
            {demoDomains.map(domain => <li key={domain.id} data-id="element-3117" data-mp="2vhfr3zi1exmy">
                <DomainCard domain={domain} highlighted={highlightedId === domain.id} onExplore={selected => {
              setHighlightedId(selected.id);
              setOpenDomain(selected);
            }} data-id="element-3118" data-mp="2ri83259lazbv" />
              </li>)}
          </ul>

          <p className="mt-10 max-w-3xl rounded-lg border border-line bg-surface-bg px-5 py-4 text-sm leading-relaxed text-ink-dark" data-id="element-3119" data-mp="2pjx3f63s73sw">
            {demoMilestoneNote}
          </p>
        </div>
      </section>

      <GovernmentFeature data-id="element-3120" data-mp="2ftcb20epavif" />

      <DemoFoundation data-id="element-3121" data-mp="2kldjfb8r0236" />

      <DomainDetailDialog domain={openDomain} onClose={closeDialog} data-id="element-3122" data-mp="2klna711mad9x" />
    </>;
}